/**
 * AnimatedSection
 * ───────────────
 * Scroll-triggered section with state transitions.
 * Supports parallax layers and ambient changes.
 */

import { useRef, useEffect, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { VVS_MOTION, prefersReducedMotion, getIntensity } from './tokens';

gsap.registerPlugin(ScrollTrigger);

export default function AnimatedSection({
  children,
  className = '',
  style = {},
  act = 2, // 1=high energy, 2=medium, 3=slow, 4=resolution
  parallax = false,
  onEnter,
  onLeave,
  ...props
}) {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  
  // Get animation intensity based on act
  const getActConfig = useCallback(() => {
    switch (act) {
      case 1: // Hero - high energy
        return { y: 80, duration: VVS_MOTION.cinematic, ease: VVS_MOTION.wave };
      case 2: // Development - medium
        return { y: 60, duration: VVS_MOTION.slow, ease: VVS_MOTION.enter };
      case 3: // Depth - slow, contemplative
        return { y: 40, duration: VVS_MOTION.cinematic, ease: VVS_MOTION.organic };
      case 4: // Resolution - return to energy
        return { y: 50, duration: VVS_MOTION.normal, ease: VVS_MOTION.spring };
      default:
        return { y: 60, duration: VVS_MOTION.normal, ease: VVS_MOTION.enter };
    }
  }, [act]);
  
  useEffect(() => {
    if (!sectionRef.current || prefersReducedMotion()) return;
    
    const intensity = getIntensity();
    const config = getActConfig();
    
    const ctx = gsap.context(() => {
      // Main reveal animation
      gsap.from(contentRef.current, {
        y: config.y * intensity,
        opacity: 0,
        duration: config.duration,
        ease: config.ease,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: VVS_MOTION.triggerStart,
          toggleActions: "play none none reverse",
          onEnter: () => onEnter?.(),
          onLeave: () => onLeave?.(),
          onEnterBack: () => onEnter?.(),
          onLeaveBack: () => onLeave?.(),
        },
      });
      
      // Parallax effect if enabled
      if (parallax) {
        gsap.to(contentRef.current, {
          y: -50 * VVS_MOTION.parallax.midground * intensity,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });
      }
    }, sectionRef);
    
    return () => ctx.revert();
  }, [getActConfig, parallax, onEnter, onLeave]);
  
  return (
    <section
      ref={sectionRef}
      className={className}
      style={{
        ...style,
        position: 'relative',
        overflow: 'hidden',
      }}
      {...props}
    >
      <div
        ref={contentRef}
        style={{
          willChange: 'transform, opacity',
        }}
      >
        {children}
      </div>
    </section>
  );
}
